import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { MapPin, Warning } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Sélecteur hiérarchique Bâtiment → Niveau → Espace → Pièce.
 *
 * Props :
 * - chantierId : string (obligatoire)
 * - value : { batiment_id, niveau, espace_id, piece_id } | null
 * - onChange : (newValue) => void
 * - mode : 'full' | 'espace' | 'piece_only' (défaut : 'full')
 * - required : bool — si true, affiche astérisques sur champs manquants
 * - label : string — label au-dessus du picker
 * - compact : bool — mode serré sur une ligne
 *
 * Comportements :
 * - Auto-collapse : chantier mono-bâtiment mono-espace → juste le dropdown Pièce
 * - Hiérarchie manquante : message + lien vers onglet Plans
 */
export default function HierarchyPicker({
  chantierId,
  value,
  onChange,
  mode = 'full',
  required = false,
  label = 'Localisation',
  compact = false,
}) {
  const [structure, setStructure] = useState(null);
  const [loading, setLoading] = useState(true);
  const v = value || {};

  useEffect(() => {
    if (!chantierId) return;
    setLoading(true);
    axios.get(`${API_URL}/api/chantiers/${chantierId}/plans-structure`, { withCredentials: true })
      .then(r => setStructure(r.data))
      .catch(() => setStructure(null))
      .finally(() => setLoading(false));
  }, [chantierId]);

  // Auto-détection mono-bâtiment mono-espace
  const monoConfig = useMemo(() => {
    if (!structure?.batiments?.length) return { mono: false };
    if (structure.batiments.length !== 1) return { mono: false };
    const bat = structure.batiments[0];
    const espaces = bat.espaces || [];
    if (espaces.length === 0) return { mono: false };
    // On considère "mono" si un seul bâtiment et si tous les espaces sont sur le même niveau
    const niveaux = new Set(espaces.map(e => e.niveau));
    if (espaces.length === 1) {
      return { mono: true, batiment: bat, niveau: [...niveaux][0], espace: espaces[0] };
    }
    return { mono: false, batiment: bat };
  }, [structure]);

  // Pièces disponibles : selon espace sélectionné OU mode piece_only (toutes)
  const allPieces = useMemo(() => {
    if (!structure?.batiments) return [];
    const out = [];
    structure.batiments.forEach(b => {
      (b.espaces || []).forEach(e => {
        (e.pieces || []).forEach(pc => {
          out.push({
            id: pc.id, nom: pc.nom, type_piece: pc.type_piece,
            batiment_id: b.id, batiment_nom: b.nom,
            niveau: e.niveau, espace_id: e.id, espace_nom: e.nom,
            label: `${pc.nom} · ${e.nom} · ${e.niveau} · ${b.nom}`,
          });
        });
      });
    });
    return out;
  }, [structure]);

  // Pour mode 'full' : listes filtrées en cascade
  const batiments = structure?.batiments || [];
  const niveauxOfBat = useMemo(() => {
    if (!v.batiment_id) return [];
    const bat = batiments.find(b => b.id === v.batiment_id);
    if (!bat) return [];
    const niv = new Map();
    (bat.espaces || []).forEach(e => {
      niv.set(e.niveau, { niveau: e.niveau, ordre: e.ordre_niveau });
    });
    return [...niv.values()].sort((a, b) => a.ordre - b.ordre);
  }, [batiments, v.batiment_id]);

  const espacesOfNiveau = useMemo(() => {
    if (!v.batiment_id || !v.niveau) return [];
    const bat = batiments.find(b => b.id === v.batiment_id);
    if (!bat) return [];
    return (bat.espaces || []).filter(e => e.niveau === v.niveau);
  }, [batiments, v.batiment_id, v.niveau]);

  const piecesOfEspace = useMemo(() => {
    if (!v.espace_id) return [];
    for (const b of batiments) {
      const e = (b.espaces || []).find(x => x.id === v.espace_id);
      if (e) return e.pieces || [];
    }
    return [];
  }, [batiments, v.espace_id]);

  if (loading) {
    return <div className="text-xs text-[#9A9A9A] italic">Chargement de la structure…</div>;
  }

  // Hiérarchie non configurée
  if (!structure || !structure.batiments || structure.batiments.length === 0 || allPieces.length === 0) {
    return (
      <div className="p-3 border border-dashed border-[#C4773B] bg-[#FFF3E0] flex items-start gap-2" data-testid="hierarchy-picker-empty">
        <Warning size={16} className="text-[#C4773B] flex-shrink-0 mt-0.5" />
        <div className="text-xs">
          <p className="font-medium text-[#6B4A24]">Hiérarchie non configurée</p>
          <p className="text-[#6B6B6B] mt-0.5">
            Aucune pièce n'a encore été créée sur ce chantier. Ouvrez l'onglet <strong>Conception → Plans</strong>
            {' '}pour créer bâtiments, espaces et pièces avant de localiser.
          </p>
        </div>
      </div>
    );
  }

  // Mode piece_only : un dropdown searchable avec toutes les pièces (label complet)
  if (mode === 'piece_only') {
    return (
      <div data-testid="hierarchy-picker-piece-only">
        {label && <label className="block text-xs text-[#6B6B6B] mb-1">{label}{required && <span className="text-[#C4773B]">*</span>}</label>}
        <select
          className="w-full p-2 border border-[#E0E0DB] text-sm rounded-none"
          value={v.piece_id || ''}
          onChange={e => {
            const picked = allPieces.find(x => x.id === e.target.value);
            if (!picked) { onChange(null); return; }
            onChange({
              batiment_id: picked.batiment_id, niveau: picked.niveau,
              espace_id: picked.espace_id, piece_id: picked.id,
            });
          }}
          data-testid="hierarchy-piece-only-select"
        >
          <option value="">— Aucune pièce —</option>
          {allPieces.map(pc => (
            <option key={pc.id} value={pc.id}>{pc.label}</option>
          ))}
        </select>
      </div>
    );
  }

  // Auto-collapse mono-bâtiment mono-espace : on n'expose que le dropdown Pièce
  if (monoConfig.mono) {
    const bat = monoConfig.batiment;
    const esp = monoConfig.espace;
    const pieces = esp.pieces || [];
    if (pieces.length === 0) {
      return <div className="text-xs text-[#9A9A9A] italic">Cet espace ne contient encore aucune pièce.</div>;
    }
    return (
      <div data-testid="hierarchy-picker-mono">
        {label && <label className="block text-xs text-[#6B6B6B] mb-1">{label}{required && <span className="text-[#C4773B]">*</span>}</label>}
        <div className="text-[11px] text-[#9A9A9A] mb-1">{bat.nom} · {esp.nom}</div>
        <select
          className="w-full p-2 border border-[#E0E0DB] text-sm rounded-none"
          value={v.piece_id || ''}
          onChange={e => {
            if (!e.target.value) { onChange(null); return; }
            onChange({
              batiment_id: bat.id, niveau: monoConfig.niveau || esp.niveau,
              espace_id: esp.id, piece_id: e.target.value,
            });
          }}
          data-testid="hierarchy-mono-piece-select"
        >
          <option value="">— Aucune pièce —</option>
          {pieces.map(pc => <option key={pc.id} value={pc.id}>{pc.nom}</option>)}
        </select>
      </div>
    );
  }

  // Mode espace : jusqu'à Espace (pas la pièce)
  // Mode full : cascade complète
  const showPiece = mode === 'full';
  const containerCls = compact ? 'grid grid-cols-4 gap-2' : 'space-y-2';

  return (
    <div data-testid="hierarchy-picker-full">
      {label && !compact && <label className="block text-xs text-[#6B6B6B] mb-1">{label}{required && <span className="text-[#C4773B]">*</span>}</label>}
      <div className={containerCls}>
        {/* Bâtiment */}
        <div>
          {compact && <label className="block text-[10px] text-[#9A9A9A] mb-0.5">Bâtiment{required && '*'}</label>}
          <select
            className="w-full p-2 border border-[#E0E0DB] text-sm rounded-none"
            value={v.batiment_id || ''}
            onChange={e => onChange({ batiment_id: e.target.value || null, niveau: null, espace_id: null, piece_id: null })}
            data-testid="hierarchy-batiment-select"
          >
            <option value="">— Bâtiment —</option>
            {batiments.map(b => <option key={b.id} value={b.id}>{b.nom}</option>)}
          </select>
        </div>
        {/* Niveau */}
        <div>
          {compact && <label className="block text-[10px] text-[#9A9A9A] mb-0.5">Niveau{required && '*'}</label>}
          <select
            className="w-full p-2 border border-[#E0E0DB] text-sm rounded-none"
            value={v.niveau || ''}
            onChange={e => onChange({ ...v, niveau: e.target.value || null, espace_id: null, piece_id: null })}
            disabled={!v.batiment_id}
            data-testid="hierarchy-niveau-select"
          >
            <option value="">— Niveau —</option>
            {niveauxOfBat.map(n => <option key={n.niveau} value={n.niveau}>{n.niveau}</option>)}
          </select>
        </div>
        {/* Espace */}
        <div>
          {compact && <label className="block text-[10px] text-[#9A9A9A] mb-0.5">Espace{required && '*'}</label>}
          <select
            className="w-full p-2 border border-[#E0E0DB] text-sm rounded-none"
            value={v.espace_id || ''}
            onChange={e => onChange({ ...v, espace_id: e.target.value || null, piece_id: null })}
            disabled={!v.niveau}
            data-testid="hierarchy-espace-select"
          >
            <option value="">— Espace —</option>
            {espacesOfNiveau.map(esp => <option key={esp.id} value={esp.id}>{esp.nom} ({esp.type})</option>)}
          </select>
        </div>
        {/* Pièce */}
        {showPiece && (
          <div>
            {compact && <label className="block text-[10px] text-[#9A9A9A] mb-0.5">Pièce{required && '*'}</label>}
            <select
              className="w-full p-2 border border-[#E0E0DB] text-sm rounded-none"
              value={v.piece_id || ''}
              onChange={e => onChange({ ...v, piece_id: e.target.value || null })}
              disabled={!v.espace_id}
              data-testid="hierarchy-piece-select"
            >
              <option value="">— Pièce —</option>
              {piecesOfEspace.map(pc => <option key={pc.id} value={pc.id}>{pc.nom}</option>)}
            </select>
          </div>
        )}
      </div>
      {/* Affichage résumé de la localisation choisie */}
      {v.piece_id && !compact && (
        <div className="mt-1 text-[11px] text-[#6B4A24] flex items-center gap-1">
          <MapPin size={12} />
          <span>Localisation enregistrée</span>
        </div>
      )}
    </div>
  );
}
